import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImageSquare, ClipboardText, Sparkle, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const EMPTY = {
  client_name: "",
  client_phone: "",
  pickup_location: "",
  dropoff_location: "",
  pickup_date: "",
  pickup_time: "",
  notes: "",
  price: "",
};

/**
 * Driver-only modal for creating / editing a personal ride. These rides live
 * in a separate backend collection and NEVER appear in admin / business /
 * payments dashboards — see Phase 33 notes in PRD.md.
 *
 * `mode="notes"` opens a quick edit-only-notes view (Phase 1 add) so the
 * driver can adjust on-the-fly information without touching the rest.
 *
 * Phase 3 — AI assist:
 *   • "Coller un texte" expands a textarea, calls
 *     POST /api/driver/personal-rides/ai-parse and prefills the form.
 *   • "Importer une capture" opens a hidden file picker, posts the image to
 *     POST /api/driver/personal-rides/ai-parse-image and prefills the form.
 *   • Price is NEVER imported by the AI (per spec — personal pricing is
 *     decoupled from S.DRIVE pricing). The driver always types it manually.
 */

// Map the AI parser's normalized payload onto the personal-ride form. Forbids
// any S.DRIVE-specific field (price, fare, commission…) leaking into the form.
function applyParsed(form, parsed) {
  if (!parsed || typeof parsed !== "object") return form;
  const p = parsed;
  const route = p.route_label || "";
  let pickup = p.pickup_location || "";
  let dropoff = p.dropoff_location || "";
  if (!pickup && !dropoff && route.includes("→")) {
    const [a, b] = route.split("→").map(s => s.trim());
    pickup = a || pickup;
    dropoff = b || dropoff;
  }
  return {
    ...form,
    client_name:     p.client_name     || form.client_name,
    client_phone:    p.client_phone    || form.client_phone,
    pickup_location: pickup            || form.pickup_location,
    dropoff_location: dropoff          || form.dropoff_location,
    pickup_date:     (p.pickup_date || form.pickup_date || "").slice(0, 10),
    pickup_time:     (p.pickup_time || form.pickup_time || "").slice(0, 5),
    notes:           p.notes           || form.notes,
    // price never comes from the AI — decoupled by design
  };
}

export default function PersonalRideModal({ open, onClose, ride, onSaved, mode = "full" }) {
  const isEdit = !!ride;
  const notesOnly = mode === "notes" && isEdit;
  const [form, setForm] = useState(ride ? {
    client_name: ride.client_name || "",
    client_phone: ride.client_phone || "",
    pickup_location: ride.pickup_location || "",
    dropoff_location: ride.dropoff_location || "",
    pickup_date: ride.pickup_date || "",
    pickup_time: ride.pickup_time || "",
    notes: ride.notes || "",
    price: ride.price != null ? String(ride.price) : "",
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  // AI assist state — hidden until the driver clicks "Coller un texte"
  const [showText, setShowText] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const parseText = async () => {
    const text = aiText.trim();
    if (text.length < 5) { toast.error("Texte trop court"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/driver/personal-rides/ai-parse", { text });
      setForm((f) => applyParsed(f, data));
      const status = data?.status || "empty";
      if (status === "empty") toast.message("Aucune information détectée");
      else toast.success("Champs pré-remplis");
      setShowText(false);
      setAiText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'analyse");
    } finally {
      setAiLoading(false);
    }
  };

  const parseImage = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image > 10 Mo"); return; }
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/driver/personal-rides/ai-parse-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => applyParsed(f, data));
      const status = data?.status || "empty";
      if (status === "empty") toast.message("Aucune information détectée");
      else toast.success("Champs pré-remplis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'analyse de l'image");
    } finally {
      setAiLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!notesOnly && (!form.pickup_date || !form.pickup_time)) {
      toast.error("Date et heure requises");
      return;
    }
    setSaving(true);
    try {
      const payload = notesOnly
        ? { notes: form.notes }
        : {
            ...form,
            price: form.price !== "" ? parseFloat(form.price) : null,
          };
      if (isEdit) {
        await api.put(`/driver/personal-rides/${ride.id}`, payload);
        toast.success(notesOnly ? "Notes enregistrées" : "Course personnelle modifiée");
      } else {
        await api.post("/driver/personal-rides", payload);
        toast.success("Course personnelle créée");
      }
      onSaved?.();
      onClose?.();
      if (!isEdit) setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="personal-ride-modal" className="bg-[#0f0f0f] border border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-base">
            {notesOnly ? "Modifier les notes"
              : isEdit ? "Modifier la course personnelle"
              : "Nouvelle course personnelle"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Privée — invisible pour le dispatch et l'administration.
          </DialogDescription>
        </DialogHeader>

        {/* Phase 3 — AI assist (creation only). Hidden in notes mode and on edit
            so we don't accidentally overwrite a saved ride's data. */}
        {!notesOnly && !isEdit && (
          <div data-testid="pr-ai-assist" className="space-y-2 border border-amber-500/20 bg-amber-500/5 rounded-md p-3">
            <div className="flex items-center gap-2">
              <Sparkle size={14} className="text-amber-400" />
              <p className="text-xs text-amber-200 font-medium">Analyse rapide (IA)</p>
            </div>
            <p className="text-[11px] text-zinc-400">Collez un texte ou importez une capture d'écran — les champs seront pré-remplis. Le prix reste à votre discrétion.</p>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" data-testid="pr-ai-paste-toggle" onClick={() => setShowText(s => !s)} variant="outline"
                className="h-9 text-xs border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                <ClipboardText size={14} className="mr-1.5" /> Coller un texte
              </Button>
              <Button type="button" data-testid="pr-ai-image-btn" onClick={() => fileRef.current?.click()} disabled={aiLoading} variant="outline"
                className="h-9 text-xs border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                <ImageSquare size={14} className="mr-1.5" /> Importer une capture
              </Button>
              <input ref={fileRef} type="file" accept="image/*" data-testid="pr-ai-image-input" className="hidden"
                onChange={(e) => parseImage(e.target.files?.[0])} />
            </div>
            {showText && (
              <div className="space-y-2">
                <Textarea data-testid="pr-ai-text" value={aiText} onChange={(e) => setAiText(e.target.value)}
                  placeholder="Collez ici un email / SMS / WhatsApp décrivant la course…"
                  className="bg-[#1A1A1A] border-zinc-700 text-white min-h-[100px] text-sm" />
                <div className="flex gap-2">
                  <Button type="button" data-testid="pr-ai-parse-text" onClick={parseText} disabled={aiLoading}
                    className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-9 text-xs">
                    {aiLoading ? "Analyse…" : "Analyser le texte"}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => { setShowText(false); setAiText(""); }}
                    className="h-9 text-xs text-zinc-400 hover:text-white">
                    <X size={14} />
                  </Button>
                </div>
              </div>
            )}
            {aiLoading && <p className="text-[11px] text-amber-300/80">Analyse en cours…</p>}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {!notesOnly && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Client</Label>
                  <Input data-testid="pr-client-name" value={form.client_name} onChange={set("client_name")} placeholder="(optionnel)"
                    className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Téléphone</Label>
                  <Input data-testid="pr-client-phone" type="tel" value={form.client_phone} onChange={set("client_phone")} placeholder="+33..."
                    className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Départ</Label>
                  <Input data-testid="pr-pickup" value={form.pickup_location} onChange={set("pickup_location")} placeholder="Adresse de départ"
                    className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Arrivée</Label>
                  <Input data-testid="pr-dropoff" value={form.dropoff_location} onChange={set("dropoff_location")} placeholder="Adresse d'arrivée"
                    className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date *</Label>
                  <Input data-testid="pr-date" type="date" value={form.pickup_date} onChange={set("pickup_date")} required
                    className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Heure *</Label>
                  <Input data-testid="pr-time" type="time" value={form.pickup_time} onChange={set("pickup_time")} required
                    className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Prix (EUR)</Label>
                  <Input data-testid="pr-price" type="number" step="0.01" value={form.price} onChange={set("price")} placeholder="0.00"
                    className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                </div>
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="pr-notes" value={form.notes} onChange={set("notes")}
              placeholder="Infos client, consignes, tarif convenu…"
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 min-h-[80px]" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-1">Annuler</Button>
            <Button type="submit" data-testid="pr-submit" disabled={saving} className="bg-amber-500 text-black hover:bg-amber-600 font-semibold flex-1">
              {saving ? "..." : isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
